import { useState, useRef, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { assessmentRbacChecker } from "../../../../../utils/rbac";
import { getUniqueListBy } from "../../../../../utils";
import { getdebridementAsync, getlistOptionsAsync, optionTypeAsync, patchDebridementAsync, setdebridementListoptions } from "../../../../../app/patientSlice";

const DEBRIDEMENT_OPTION_TYPES = [
  "debridementperformedby",
  "nonexcisionalapproach",
  "surgicaldebridementprocedure",
  "depthofdebridement",
  "instrumentsused",
  "necroticsubcutaneoustissue",
  "necrotictissue",
  "postdebridementbleedingamount",
  "techniqueused",
  "notapplicable",
];

export const useDebridement = ({ interventionId, formularyId }) => {
  const { register, handleSubmit, reset, errors, getValues } = useForm();
  const dispatch = useDispatch();
  const { patientId, assessmentId } = useParams();

  // ── Confirmation modal state ────────────────────────────────────────────────
  const [editable, setEditable] = useState(false);
  const [formChangesConfirm, setFormChangesConfirm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [clickCancel, setClickCancel] = useState(false);

  // ── Multi-select data state ─────────────────────────────────────────────────
  const [listViewMenuData, setListViewMenuData] = useState([]);
  const [necroticTissueTmp, setNecroticTissueTmp] = useState([]);
  const [instrumentsUsedTemp, setInstrumentsUsedTemp] = useState([]);
  const [notapplicableTmp, setNotapplicableTmp] = useState();

  // ── UI visibility flags ─────────────────────────────────────────────────────
  const [noDataMsg, setNoDataMsg] = useState(false);
  const [additionalQuestions, setAdditionalQuestions] = useState(false);
  const [excisionalDisable, setExcisionalDisable] = useState(false);
  const [instrumentsUsed, setInstrumentsUsed] = useState(false);
  const [ftNecroticSubcutaneousTissue, setFtNecroticSubcutaneousTissue] = useState(false);
  const [necroticTissueView, setNecroticTissueView] = useState(false);
  const [na, setNa] = useState(false);
  const [techniqueUsed, setTechniqueUsed] = useState(false);
  const [stoppedBleeding, setStoppedBleeding] = useState(false);
  const [customErrors, setCustomErrors] = useState(false);
  const [nonExcisionalShow, setNonExcisionalShow] = useState();
  const [deepestLevelCom, setDeepestLevelCom] = useState();
  const [surfaceAreaCheck, setSurfaceAreaCheck] = useState(false);
  const [prodCatCheck, setProdCatCheck] = useState(false);
  const [nonExcisionalDisabledValues, setNonExcisionalDisabledValues] = useState("");

  // ── Dropdown selection tracking ─────────────────────────────────────────────
  const [performedByValue, setPerformedByValue] = useState();
  const [debridementProcedureValue, setDebridementProcedureValue] = useState();
  const [getValue, setGetValue] = useState([]);

  // ── Default values for multi-selects ───────────────────────────────────────
  const resp = useSelector((state) => state.patient.debridement);
  const [instrumentsUsedDefault, setInstrumentsUsedDefault] = useState(resp?.instrumentsUsed);
  const [defaultValNecroticTissue, setDefaultValNecroticTissue] = useState(resp?.necroticTissue);
  const [defaultValNecroticSubTissue, setDefaultValNecroticSubTissue] = useState(resp?.necroticSubcutaneousTissue);
  const [defaultValNotApplicable, setDefaultValNotApplicable] = useState(resp?.notApplicable);

  // ── Refs for MultiSelect reset ──────────────────────────────────────────────
  const listViewRef = useRef(null);
  const instrumentsUsedRef = useRef(null);
  const necroticTissueRef = useRef(null);
  const notapplicableRef = useRef(null);

  // ── Redux selectors ─────────────────────────────────────────────────────────
  const listOptionsresp = useSelector((state) => state.patient.debridementListoptions);
  const patientData = useSelector((state) => state.patient.detailViewData);
  const userDetails = useSelector((state) => state.user);
  const debridementperformedbyOptions = useSelector((state) => state.patient.debridementperformedby);
  const nonexcisionalapproachOptions = useSelector((state) => state.patient.nonexcisionalapproach);
  const surgicaldebridementprocedureOptions = useSelector((state) => state.patient.surgicaldebridementprocedure);
  const depthofdebridementOptions = useSelector((state) => state.patient.depthofdebridement);
  const instrumentsusedOptions = useSelector((state) => state.patient.instrumentsused);
  const necroticsubcutaneoustissueOptions = useSelector((state) => state.patient.necroticsubcutaneoustissue);
  const necrotictissueOptions = useSelector((state) => state.patient.necrotictissue);
  const postdebridementbleedingamountOptions = useSelector((state) => state.patient.postdebridementbleedingamount);
  const techniqueusedOptions = useSelector((state) => state.patient.techniqueusedOptions);
  const notapplicableOptions = useSelector((state) => state.patient.notapplicableOptions);

  const hideButtons = assessmentRbacChecker(userDetails?.role, patientData?.isOwnCustomer);

  // ── Confirmation modal handlers ─────────────────────────────────────────────
  const confirmationClose = useCallback(() => {
    setConfirmationModal(false);
    setClickCancel(false);
  }, []);

  const yesConfirmationModalClick = useCallback(() => {
    setConfirmationModal(false);
    setEditable(false);
    setFormChangesConfirm(false);
    setClickCancel(false);
  }, []);

  // ── Multi-select helpers ────────────────────────────────────────────────────
  const buildMultiSelectArr = useCallback((e, ref) => {
    if (e?.length > 0) {
      let arr = [];
      Array.isArray(e) &&
        e.forEach((data) => {
          if (data.key !== "none") {
            arr.push(data.key);
          } else {
            ref?.current?.resetSelectedValues();
            arr = [data.key];
          }
        });
      return arr;
    }
    return [];
  }, []);

  // ── Edit mode ───────────────────────────────────────────────────────────────
  const editMode = useCallback(() => {
    setFormChangesConfirm(false);
    setClickCancel(false);
    setEditable(true);
    setInstrumentsUsedDefault(resp?.instrumentsUsed);
    setInstrumentsUsedTemp(resp?.instrumentsUsed);
    setListViewMenuData(resp?.necroticSubcutaneousTissue);
    setNecroticTissueTmp(resp?.necroticTissue);
    setNotapplicableTmp(resp?.notApplicable);
    setDefaultValNecroticSubTissue(resp?.necroticSubcutaneousTissue);
    setDefaultValNecroticTissue(resp?.necroticTissue);
    setDefaultValNotApplicable(resp?.notApplicable);

    resp?.isDebridementPerformed === "yes" ? setAdditionalQuestions(true) : setAdditionalQuestions(false);

    if (performedByValue === "ptorrn-certified(cwswccwocn)" && debridementProcedureValue === "excisional(cutaway-greaterthan1.0cm²)") {
      setDeepestLevelCom(true);
    }
    if (performedByValue === "ptorrn-certified(cwswccwocn)" && debridementProcedureValue === "non-excisional(abradescrapesnippick)") {
      setExcisionalDisable(false);
      setNonExcisionalShow(true);
    }
    if (performedByValue !== "ptorrn-certified(cwswccwocn)" && debridementProcedureValue === "excisional(cutaway-greaterthan1.0cm²)") {
      setNonExcisionalShow(false);
      setDeepestLevelCom(true);
    }
    if (performedByValue !== "ptorrn-certified(cwswccwocn)" && debridementProcedureValue === "non-excisional(abradescrapesnippick)") {
      setNonExcisionalShow(true);
      setDeepestLevelCom(false);
      setExcisionalDisable(false);
    }
    if (resp?.depthOfDebridement !== "na") {
      setNotapplicableTmp([]);
    }

    resp?.postDebridementBleedingAmount === "small" ||
    resp?.postDebridementBleedingAmount === "moderate" ||
    resp?.postDebridementBleedingAmount === "large"
      ? setStoppedBleeding(true)
      : setStoppedBleeding(false);

    resp?.postDebridementBleedingAmount === "none" ? setTechniqueUsed(true) : setTechniqueUsed(false);

    resp?.depthOfDebridement === "na" ||
    resp?.depthOfDebridement === "fullthickness-necrotictissue" ||
    resp?.depthOfDebridement === "fullthickness-necroticsubcutaneoustissue" ||
    resp?.depthOfDebridement === "superficial-skin" ||
    resp?.depthOfDebridement === "partialthickness-necroticskin"
      ? setInstrumentsUsed(true)
      : setInstrumentsUsed(false);

    resp?.depthOfDebridement === "fullthickness-necroticsubcutaneoustissue"
      ? setFtNecroticSubcutaneousTissue(true)
      : setFtNecroticSubcutaneousTissue(false);

    resp?.depthOfDebridement === "fullthickness-necrotictissue" ? setNecroticTissueView(true) : setNecroticTissueView(false);

    resp?.depthOfDebridement === "na" ? setNa(true) : setNa(false);

    resp?.surgicalDebridementProcedure === "excisional(cutaway-greaterthan1.0cm²)" ? setDeepestLevelCom(true) : setDeepestLevelCom(false);

    resp?.surgicalDebridementProcedure === "non-excisional(abradescrapesnippick)" ? setNonExcisionalShow(true) : setNonExcisionalShow(false);

    setTimeout(() => {
      if (document.getElementById("deepestLevel") && resp?.depthOfDebridement) {
        document.getElementById("deepestLevel").value = resp?.depthOfDebridement;
      }
    }, 10);

    if (resp?.depthOfDebridement && resp?.depthOfDebridement !== "") {
      setSurfaceAreaCheck(true);
      setInstrumentsUsed(true);
    } else {
      setInstrumentsUsed(false);
    }

    if (resp?.isDebridementPerformed) {
      setProdCatCheck(true);
    }

    if (resp?.isDebridementPerformed === "yes") {
      setNonExcisionalDisabledValues("");
      setTimeout(() => {
        if (document.getElementById("surgicalDebridementPerformed")) {
          document.getElementById("surgicalDebridementPerformed").value = "yes";
        } else {
          document.getElementById("surgicalDebridementPerformed").value = "no";
        }
        if (resp?.debridementPerformedBy) {
          if (document.getElementById("performed")) {
            document.getElementById("performed").value = resp?.debridementPerformedBy;
          }
          if (resp?.debridementPerformedBy === "ptorrn-certified(cwswccwocn)") {
            setNonExcisionalShow(true);
            if (document.getElementById("nonExcisionalId")) {
              document.getElementById("nonExcisionalId").value = resp?.nonExcisionalApproach;
            }
            if (resp?.surgicalDebridementProcedure === "non-excisional(abradescrapesnippick)") {
              setNonExcisionalDisabledValues("percutaneous");
            } else {
              setNonExcisionalDisabledValues("open");
            }
          }
        }
        if (resp?.surgicalDebridementProcedure) {
          if (document.getElementById("excisional")) {
            document.getElementById("excisional").value = resp?.surgicalDebridementProcedure;
          }
          if (resp?.surgicalDebridementProcedure === "non-excisional(abradescrapesnippick)" && resp?.nonExcisionalApproach) {
            if (document.getElementById("nonExcisionalId")) {
              document.getElementById("nonExcisionalId").value = resp?.nonExcisionalApproach;
            }
          } else {
            if (document.getElementById("text") && resp?.surfaceAreaOfNecroticSkinTissue) {
              document.getElementById("text").value = resp?.surfaceAreaOfNecroticSkinTissue;
            }
            if (document.getElementById("subqTissue") && resp?.surfaceAreaOfNecroticSubqTissue) {
              document.getElementById("subqTissue").value = resp?.surfaceAreaOfNecroticSubqTissue;
            }
            if (document.getElementById("bleedingAmount") && resp?.postDebridementBleedingAmount) {
              document.getElementById("bleedingAmount").value = resp?.postDebridementBleedingAmount;
            }
            if (resp?.postDebridementBleedingAmount && resp?.postDebridementBleedingAmount === "none") {
              if (document.getElementById("techniqueUsed") && resp?.techniqueUsed) {
                document.getElementById("techniqueUsed").value = resp?.techniqueUsed;
              }
            }
            if (resp?.postDebridementBleedingAmount && resp?.postDebridementBleedingAmount !== "none") {
              if (document.getElementById("SPWConactPressure0") && resp?.stoppedBleedingContactPressure) {
                document.getElementById("SPWConactPressure0").value = resp?.stoppedBleedingContactPressure;
                document.getElementById("SPWConactPressure0").click();
              }
            }
          }
        }
      }, 10);
    }

    if (resp?.productCategory?.length > 0) {
      setGetValue(resp?.productCategory);
    }
  }, [resp, performedByValue, debridementProcedureValue]);

  // ── View mode ───────────────────────────────────────────────────────────────
  const viewMode = useCallback(() => {
    // intentionally empty — kept for future restoration as in original
  }, []);

  // ── Primary group (yes/no debridement) ─────────────────────────────────────
  const getPrimaryGroup = useCallback(
    (e) => {
      e.target.value === "yes" ? setAdditionalQuestions(true) : setAdditionalQuestions(false);
      if (e?.target?.value === "no") {
        setInstrumentsUsed(false);
        reset({
          ...getValues(),
          DebridementPerformed: "",
          performedExcisional: "",
          deepestLevel: "",
          DebridedOfNecroticSkin: "",
        });
      }
      setProdCatCheck(true);
    },
    [reset, getValues]
  );

  // ── Performed by change ─────────────────────────────────────────────────────
  const surgicalPerformedBy = useCallback(
    (e) => {
      const changedVal = e.target.value;
      reset({
        ...getValues(),
        performedExcisional: "",
        nonExcisional: "",
        deepestLevel: "",
        DebridedOfNecroticSkin: "",
        subqTissue: "",
        bleedingAmount: "",
        techniqueUsed: "",
        SPWConactPressure: "",
      });
      instrumentsUsedRef?.current?.resetSelectedValues([]); // NOSONAR
      setInstrumentsUsedTemp([]);
      setInstrumentsUsedDefault(resp?.instrumentsUsed);
      setExcisionalDisable(false);
      setPerformedByValue(changedVal);

      if (changedVal !== "ptorrn-certified(cwswccwocn)" && debridementProcedureValue === "excisional(cutaway-greaterthan1.0cm²)") {
        setNonExcisionalShow(false);
        setDeepestLevelCom(true);
      }
      if (changedVal !== "ptorrn-certified(cwswccwocn)" && debridementProcedureValue === "non-excisional(abradescrapesnippick)") {
        setNonExcisionalShow(true);
        setExcisionalDisable(false);
        setDeepestLevelCom(false);
      }
      if (changedVal === "ptorrn-certified(cwswccwocn)" && debridementProcedureValue === "excisional(cutaway-greaterthan1.0cm²)") {
        setNonExcisionalShow(true);
        setDeepestLevelCom(true);
        setTimeout(() => {
          if (document.getElementById("nonExcisionalId")) {
            document.getElementById("nonExcisionalId").value = "percutaneous";
          }
        }, 10);
      }
      if (changedVal === "ptorrn-certified(cwswccwocn)" && debridementProcedureValue === "non-excisional(abradescrapesnippick)") {
        setExcisionalDisable(false);
        setNonExcisionalShow(true);
        setDeepestLevelCom(false);
        if (document.getElementById("nonExcisionalId")) {
          document.getElementById("nonExcisionalId").value = resp?.nonExcisionalApproach;
        }
      }
    },
    [reset, getValues, resp, debridementProcedureValue]
  );

  // ── Procedure change ────────────────────────────────────────────────────────
  const debrideProcedureChange = useCallback(
    (e) => {
      setNonExcisionalDisabledValues("");
      setDebridementProcedureValue(e.target.value);
      setInstrumentsUsedDefault(resp?.instrumentsUsed);
      setExcisionalDisable(false);

      if (performedByValue !== "ptorrn-certified(cwswccwocn)" && e.target.value === "excisional(cutaway-greaterthan1.0cm²)") {
        setNonExcisionalShow(false);
        setDeepestLevelCom(true);
        if (document.getElementById("nonExcisionalId")) {
          document.getElementById("nonExcisionalId").value = "";
        }
      }
      if (performedByValue !== "ptorrn-certified(cwswccwocn)" && e.target.value === "non-excisional(abradescrapesnippick)") {
        setInstrumentsUsedDefault(resp?.instrumentsUsed);
        setInstrumentsUsedTemp([]);
        reset({ ...getValues(), deepestLevel: "" });
        setNonExcisionalShow(true);
        setDeepestLevelCom(false);
        setTimeout(() => {
          if (document.getElementById("nonExcisionalId")) {
            document.getElementById("nonExcisionalId").value = resp?.nonExcisionalApproach ? resp?.nonExcisionalApproach : "percutaneous";
          }
        }, 10);
      }
      if (performedByValue === "ptorrn-certified(cwswccwocn)" && e.target.value === "excisional(cutaway-greaterthan1.0cm²)") {
        setNonExcisionalShow(true);
        setDeepestLevelCom(true);
        setTimeout(() => {
          if (document.getElementById("nonExcisionalId")) {
            document.getElementById("nonExcisionalId").value = "percutaneous";
            setNonExcisionalDisabledValues("open");
          }
        }, 10);
      }
      if (performedByValue === "ptorrn-certified(cwswccwocn)" && e.target.value === "non-excisional(abradescrapesnippick)") {
        setInstrumentsUsedDefault(resp?.instrumentsUsed);
        setInstrumentsUsedTemp([]);
        setListViewMenuData([]);
        setDeepestLevelCom(false);
        setNonExcisionalShow(true);
        if (document.getElementById("nonExcisionalId")) {
          document.getElementById("nonExcisionalId").value = "open";
          setNonExcisionalDisabledValues("percutaneous");
        }
      }
    },
    [resp, performedByValue, reset, getValues]
  );

  // ── Depth of debridement change ─────────────────────────────────────────────
  const performedExcisionalChange = useCallback((e) => {
    const getVal = e.target.value;
    if (getVal !== "") {
      setFtNecroticSubcutaneousTissue(false);
      setNecroticTissueView(false);
      setNa(false);
      setInstrumentsUsed(getVal !== "");
    }
    if (getVal === "") {
      setFtNecroticSubcutaneousTissue(false);
      setNecroticTissueView(false);
      setNa(false);
      setInstrumentsUsed(false);
      setNecroticTissueTmp([]);
      setNotapplicableTmp([]);
    }
    if (getVal === "fullthickness-necroticsubcutaneoustissue") {
      setFtNecroticSubcutaneousTissue(true);
      setNecroticTissueView(false);
      setNa(false);
      setDefaultValNecroticTissue(true);
      setNecroticTissueTmp([]);
      setNotapplicableTmp([]);
    }
    if (getVal === "fullthickness-necrotictissue") {
      setNecroticTissueView(true);
      setFtNecroticSubcutaneousTissue(false);
      setNa(false);
      setListViewMenuData([]);
      setNotapplicableTmp([]);
    }
    if (getVal === "na") {
      setNa(true);
      setNecroticTissueView(false);
      setFtNecroticSubcutaneousTissue(false);
      setDefaultValNecroticTissue(true);
      setListViewMenuData([]);
      setNecroticTissueTmp([]);
    }
    setSurfaceAreaCheck(true);
  }, []);

  // ── Post bleeding select ────────────────────────────────────────────────────
  const postBleedingSelect = useCallback(
    (e) => {
      const selectVal = e.target.value;
      if (selectVal === "small" || selectVal === "moderate" || selectVal === "large") {
        setStoppedBleeding(true);
        setTechniqueUsed(false);
        reset({ ...getValues(), techniqueUsed: "" });
      }
      if (selectVal === "none") {
        setTechniqueUsed(true);
        setStoppedBleeding(false);
        reset({ ...getValues(), SPWConactPressure: "" });
      }
    },
    [reset, getValues]
  );

  // ── Multi-select change handlers ────────────────────────────────────────────
  const handleListViewMenuChange = useCallback((e) => setListViewMenuData(buildMultiSelectArr(e, listViewRef)), [buildMultiSelectArr]);

  const necroticTissueChange = useCallback((e) => setNecroticTissueTmp(buildMultiSelectArr(e, necroticTissueRef)), [buildMultiSelectArr]);

  const instrumentsUsedChange = useCallback((e) => setInstrumentsUsedTemp(buildMultiSelectArr(e, instrumentsUsedRef)), [buildMultiSelectArr]);

  const notapplicableSelect = useCallback((e) => setNotapplicableTmp(buildMultiSelectArr(e, notapplicableRef)), [buildMultiSelectArr]);

  // ── Product category option value ───────────────────────────────────────────
  const getOptValue = useCallback(
    (index, name, optionsVal, isRecommended) => {
      const getOpt = {
        key: name,
        value: optionsVal || "",
        isRecommended: isRecommended || 0,
      };
      const merged = [...getValue, getOpt];
      const deduped = [];
      merged.forEach((val) => {
        if (val.key === getOpt.key) {
          deduped.push({ ...getOpt });
        } else {
          deduped.push({ ...val });
        }
      });
      setGetValue(getUniqueListBy(deduped, "key"));
    },
    [getValue]
  );

  // ── Submit ──────────────────────────────────────────────────────────────────
  const doSubmit = useCallback(
    (data) => {
      const payloadData = {
        isDebridementPerformed: data.surgicalDebridementPerformed || "",
        debridementPerformedBy: data.DebridementPerformed || "",
        surgicalDebridementProcedure: data.performedExcisional || "",
        nonExcisionalApproach: nonExcisionalShow ? data?.nonExcisional || "percutaneous" : "",
        necroticSubcutaneousTissue: listViewMenuData || "",
        necroticTissue: necroticTissueTmp || "",
        notApplicable: notapplicableTmp || [],
        depthOfDebridement: data.deepestLevel || "",
        instrumentsUsed: instrumentsUsedTemp || "",
        surfaceAreaOfNecroticSkinTissue: data.DebridedOfNecroticSkin || "",
        surfaceAreaOfNecroticSubqTissue: data.subqTissue || "",
        postDebridementBleedingAmount: data.bleedingAmount || "",
        stoppedBleedingContactPressure: data.SPWConactPressure || "",
        techniqueUsed: data.techniqueUsed || "",
        honeyDressing: null,
        productCategory: getValue,
        patientId,
        assessmentId,
        interventionId,
      };
      dispatch(patchDebridementAsync(payloadData));
      reset();
      setEditable(false);
      setAdditionalQuestions(false);
      setInstrumentsUsed(false);
      setFtNecroticSubcutaneousTissue(false);
      setNecroticTissueView(false);
      setNa(false);
      setTechniqueUsed(false);
      setStoppedBleeding(false);
      setDefaultValNecroticTissue(false);
    },
    [
      dispatch,
      reset,
      patientId,
      assessmentId,
      interventionId,
      nonExcisionalShow,
      listViewMenuData,
      necroticTissueTmp,
      notapplicableTmp,
      instrumentsUsedTemp,
      getValue,
    ]
  );

  // ── Cancel click handler ────────────────────────────────────────────────────
  const handleCancelClick = useCallback(() => {
    if (resp === null) return false;
    setClickCancel(true);
  }, [resp]);

  // ── Effects ─────────────────────────────────────────────────────────────────

  // Cancel-confirmation gate
  useEffect(() => {
    if (!editable) return;
    if (formChangesConfirm && clickCancel) {
      setConfirmationModal(true);
    } else if (clickCancel && !formChangesConfirm) {
      setClickCancel(false);
      setConfirmationModal(false);
      setEditable(false);
    }
  }, [formChangesConfirm, clickCancel, editable]);

  // Formulary list options
  useEffect(() => {
    dispatch(setdebridementListoptions({}));
    if (formularyId !== null) {
      dispatch(getlistOptionsAsync({ formularyid: formularyId, option: "debridement" }));
    }
  }, [dispatch, formularyId]);

  // One-time initialization
  useEffect(() => {
    formularyId == null || resp?.data == null ? setNoDataMsg(true) : setNoDataMsg(false);
    setAdditionalQuestions(false);
    setInstrumentsUsed(false);
    setFtNecroticSubcutaneousTissue(false);
    setNecroticTissueView(false);
    setNa(false);
    setTechniqueUsed(false);
    setStoppedBleeding(false);
    reset();
    setCustomErrors(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch debridement data
  useEffect(() => {
    viewMode();
    dispatch(getdebridementAsync({ patientId, assessmentId, interventionId }));
  }, [interventionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch all option types
  useEffect(() => {
    DEBRIDEMENT_OPTION_TYPES.forEach((type) => dispatch(optionTypeAsync(type)));
  }, [interventionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync resp → local dropdown tracking state
  useEffect(() => {
    setPerformedByValue(resp?.debridementPerformedBy);
    setDebridementProcedureValue(resp?.surgicalDebridementProcedure);
    if (resp?.productCategory?.length > 0) {
      setGetValue(resp?.productCategory);
    }
  }, [resp]);

  // Open edit when resp is null
  useEffect(() => {
    resp == null ? setEditable(true) : setEditable(false);
  }, [resp]);

  return {
    // form
    register,
    handleSubmit,
    errors,
    // state
    editable,
    confirmationModal,
    formChangesConfirm,
    setFormChangesConfirm,
    additionalQuestions,
    excisionalDisable,
    nonExcisionalShow,
    nonExcisionalDisabledValues,
    deepestLevelCom,
    instrumentsUsed,
    ftNecroticSubcutaneousTissue,
    necroticTissueView,
    na,
    techniqueUsed,
    stoppedBleeding,
    customErrors,
    surfaceAreaCheck,
    prodCatCheck,
    noDataMsg,
    // default values for multi-selects
    instrumentsUsedDefault,
    defaultValNecroticTissue,
    defaultValNecroticSubTissue,
    defaultValNotApplicable,
    getValue,
    // refs
    listViewRef,
    instrumentsUsedRef,
    necroticTissueRef,
    notapplicableRef,
    // selectors
    resp,
    listOptionsresp,
    hideButtons,
    debridementperformedbyOptions,
    nonexcisionalapproachOptions,
    surgicaldebridementprocedureOptions,
    depthofdebridementOptions,
    instrumentsusedOptions,
    necroticsubcutaneoustissueOptions,
    necrotictissueOptions,
    postdebridementbleedingamountOptions,
    techniqueusedOptions,
    notapplicableOptions,
    // handlers
    editMode,
    confirmationClose,
    yesConfirmationModalClick,
    handleCancelClick,
    getPrimaryGroup,
    surgicalPerformedBy,
    debrideProcedureChange,
    performedExcisionalChange,
    postBleedingSelect,
    handleListViewMenuChange,
    necroticTissueChange,
    instrumentsUsedChange,
    notapplicableSelect,
    getOptValue,
    doSubmit,
  };
};
